import type { Game, Provider, ProviderSearchResult } from "../types/game";

interface SteamSearchHit {
  appid: number;
  name: string;
  details?: SteamAppDetails;
}

interface SteamAppDetails {
  name: string;
  steam_appid: number;
  short_description?: string;
  header_image?: string;
  screenshots?: Array<{ path_full: string }>;
  movies?: Array<{ webm?: { max: string }; mp4?: { max: string } }>;
  release_date?: { date: string };
  genres?: Array<{ description: string }>;
  categories?: Array<{ description: string }>;
  platforms?: { windows: boolean; mac: boolean; linux: boolean };
  metacritic?: { score: number };
}

function normalize(hit: SteamSearchHit): Game {
  const d = hit.details;
  const platforms: string[] = [];
  if (d?.platforms?.windows) platforms.push("PC (Windows)");
  if (d?.platforms?.mac) platforms.push("PC (Mac)");
  if (d?.platforms?.linux) platforms.push("PC (Linux)");

  const tags = d?.categories?.map((c) => c.description) ?? [];

  let releaseYear: number | undefined;
  if (d?.release_date?.date) {
    const m = d.release_date.date.match(/\d{4}/);
    if (m) releaseYear = Number(m[0]);
  }

  return {
    id: `steam:${hit.appid}`,
    source: "steam",
    sourceId: String(hit.appid),
    title: d?.name ?? hit.name,
    description: d?.short_description,
    platforms,
    genres: d?.genres?.map((g) => g.description) ?? [],
    tags,
    releaseYear,
    imageUrl: d?.header_image,
    rating: d?.metacritic?.score,
    stores: ["Steam"],
    screenshots: d?.screenshots?.map((s) => s.path_full),
  };
}

export const steamProvider: Provider = {
  id: "steam",
  label: "Steam",
  blurb:
    "Storefront data (PC only) including multiplayer categories. Needs a proxy because store.steampowered.com blocks CORS.",
  async search(query: string): Promise<ProviderSearchResult> {
    const res = await fetch(
      `/api/steam?q=${encodeURIComponent(query)}`,
    );
    if (!res.ok) {
      const text = await res.text();
      throw new Error(
        `Steam proxy failed: ${res.status} ${res.statusText} — ${text}`,
      );
    }
    const raw = (await res.json()) as SteamSearchHit[];
    return { raw, games: raw.map(normalize) };
  },
  notes: {
    requiresApiKey: false,
    callableFromBrowser: "with-proxy",
    corsStatus: "blocked",
    needsProxy: true,
    provides: [
      "title",
      "short description",
      "PC platforms (Win/Mac/Linux)",
      "genres",
      "categories (Online Co-op / Local Co-op / Split-Screen / PvP / etc.)",
      "screenshots",
      "trailers (movies[].webm/mp4)",
      "metacritic score",
      "release date",
      "Steam store link",
    ],
    missing: [
      "console platforms (PC only)",
      "crossplay flag (must derive heuristically)",
      "Game Pass / PS Plus",
      "structured tag taxonomy (`categories` is the closest)",
    ],
    viability: "ok",
    summary:
      "Excellent for PC-multiplayer signals (`categories` includes 'Online Co-op', 'Shared/Split Screen Co-op', 'Cross-Platform Multiplayer'). Useless for console-only platforms. Pair with RAWG or IGDB. Browser calls fail CORS, so proxy via functions/api/steam.ts.",
  },
};
